import React, { useState, useEffect } from 'react';
import { Cookie, Settings, Shield, Check, X, AlertCircle, Info, ChevronDown, ChevronUp, Download, Share2, Lock, Eye, Clock, Users } from 'lucide-react';

const CookiesPolicy = () => {
  const [openSections, setOpenSections] = useState({});
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    marketing: false
  });
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    // Check if user has already made cookie choices
    const hasConsent = localStorage.getItem('cookieConsent');
    if (!hasConsent) {
      setShowBanner(true);
    }
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handlePreferenceChange = (cookieType) => {
    if (cookieType === 'necessary') return; // Necessary cookies can't be disabled
    
    setCookiePreferences(prev => ({
      ...prev,
      [cookieType]: !prev[cookieType]
    }));
  };

  const savePreferences = () => {
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify(cookiePreferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Here you would typically implement the actual cookie setting logic
    console.log('Cookie preferences saved:', cookiePreferences);
  };

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      marketing: true
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    setShowBanner(false);
    
    // Implement actual cookie setting
    console.log('All cookies accepted');
  };

  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      marketing: false
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem('cookieConsent', 'given');
    localStorage.setItem('cookiePreferences', JSON.stringify(onlyNecessary));
    setShowBanner(false);
    
    // Implement actual cookie setting
    console.log('Only necessary cookies accepted');
  };

  const cookieTypes = [
    {
      id: 'necessary',
      name: 'Necessary Cookies',
      description: 'Essential for the website to function properly',
      required: true,
      duration: 'Session to 1 year',
      examples: ['Authentication', 'Security', 'Load balancing']
    },
    {
      id: 'functional',
      name: 'Functional Cookies',
      description: 'Remember your preferences and settings',
      required: false,
      duration: '1 year',
      examples: ['Language settings', 'Region selection', 'Layout preferences']
    },
    {
      id: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how visitors interact with our website',
      required: false,
      duration: '2 years',
      examples: ['Visitor counts', 'Page views', 'User behavior']
    },
    {
      id: 'marketing',
      name: 'Marketing Cookies',
      description: 'Used to track visitors across websites for advertising',
      required: false,
      duration: '1 year',
      examples: ['Ad targeting', 'Campaign measurement', 'Interest-based ads']
    }
  ];

  const sections = [
    {
      id: 'what-are-cookies',
      title: '1. What Are Cookies?',
      content: `Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit our website. They are widely used to make websites work more efficiently and provide information to the website owners.

Cookies typically contain:
- The name of the server the cookie was sent from
- The lifetime of the cookie
- A value - usually a randomly generated unique number

The website uses this information to remember your visit and preferences when you return.`
    },
    {
      id: 'how-we-use',
      title: '2. How We Use Cookies',
      content: `We use cookies for several purposes:

**Essential Operation:**
- Keeping you signed in and maintaining your session
- Remembering items in your shopping cart
- Ensuring website security and preventing fraud
- Load balancing across our servers

**Performance and Analytics:**
- Understanding how visitors use our website
- Identifying which features are most popular
- Measuring website performance and speed
- Improving user experience and functionality

**Personalization:**
- Remembering your preferences and settings
- Customizing content based on your interests
- Providing localized experiences
- Saving your language and region choices`
    },
    {
      id: 'cookie-types',
      title: '3. Types of Cookies We Use',
      content: `We use the following categories of cookies:

**First-Party Cookies:**
These are set by our website and can only be read by our site. They're essential for basic functionality.

**Third-Party Cookies:**
These are set by trusted partners and help us provide additional features and analyze website usage.

**Session Cookies:**
Temporary cookies that expire when you close your browser. Used for maintaining your session.

**Persistent Cookies:**
Remain on your device for a set period or until you delete them. Remember your preferences.`
    },
    {
      id: 'specific-cookies',
      title: '4. Specific Cookies We Use',
      content: `Here are the specific cookies currently active on our website:

**Authentication Cookies:**
- 'auth_token' - Maintains your login session
- 'user_preferences' - Stores your site settings

**Analytics Cookies:**
- 'ga_measurement_id' - Google Analytics tracking
- 'visit_count' - Tracks returning visitors

**Functional Cookies:**
- 'language' - Remembers your language choice
- 'theme' - Stores your theme preference

**Marketing Cookies:**
- 'ad_campaign' - Tracks marketing campaign effectiveness
- 'user_segment' - Groups users for personalized content`
    },
    {
      id: 'cookie-duration',
      title: '5. Cookie Duration',
      content: `Cookies have different lifespans:

**Session Cookies:**
- Duration: Until browser closure
- Purpose: Temporary session management
- Examples: Shopping cart, form data

**Short-term Cookies:**
- Duration: 24 hours to 1 week
- Purpose: Temporary preferences
- Examples: Recently viewed items

**Long-term Cookies:**
- Duration: 1 month to 2 years
- Purpose: Persistent settings
- Examples: Language, theme, analytics

**Persistent Cookies:**
- Duration: Until manually deleted
- Purpose: Long-term preferences
- Examples: Login remember me`
    },
    {
      id: 'third-party',
      title: '6. Third-Party Cookies',
      content: `We work with trusted third parties that may set cookies:

**Analytics Providers:**
- Google Analytics: Website usage statistics
- Hotjar: User behavior analysis
- Mixpanel: Product analytics

**Advertising Partners:**
- Google Ads: Campaign measurement
- Facebook Pixel: Ad targeting
- LinkedIn Insight: Professional audience targeting

**Service Providers:**
- Stripe: Payment processing
- Intercom: Customer support
- SendGrid: Email delivery

All third-party providers are compliant with privacy regulations and offer opt-out mechanisms.`
    },
    {
      id: 'managing-cookies',
      title: '7. Managing Your Cookie Preferences',
      content: `You have several options to control cookies:

**Browser Settings:**
Most web browsers allow you to:
- View and manage existing cookies
- Block all or certain types of cookies
- Delete cookies automatically on browser close
- Set preferences for specific websites

**Our Preference Center:**
Use the cookie settings panel to:
- Choose which cookie categories to allow
- Update your preferences at any time
- See detailed information about each cookie type

**Opt-out Tools:**
- Digital Advertising Alliance opt-out
- Network Advertising Initiative opt-out
- Google Analytics opt-out browser add-on

Note: Blocking essential cookies may impact website functionality.`
    },
    {
      id: 'updates',
      title: '8. Policy Updates',
      content: `We may update this Cookie Policy periodically to reflect:

**Service Changes:**
- New features requiring additional cookies
- Changes in third-party service providers
- Updates to compliance requirements

**Legal Requirements:**
- Changes in privacy regulations
- New legal obligations
- Industry best practices

We will notify you of significant changes through:
- Website banners and notifications
- Email communications (if subscribed)
- Updated policy date stamps

Continued use of our website after changes constitutes acceptance of the updated policy.`
    }
  ];

  const lastUpdated = "January 15, 2024";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30">
      {/* Cookie Banner */}
      {showBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
          <div className="max-w-7xl mx-auto p-4">
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 p-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 text-lg mb-2">We Use Cookies</h3>
                  <p className="text-gray-600 mb-4">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. You can manage your preferences at any time.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={acceptAll}
                      className="bg-blue-600 text-white px-6 py-2 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                    >
                      Accept All
                    </button>
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="bg-white text-blue-600 px-6 py-2 rounded-xl font-semibold border border-blue-600 hover:bg-blue-50 transition-colors"
                    >
                      Customize Settings
                    </button>
                    <button
                      onClick={rejectAll}
                      className="bg-gray-100 text-gray-700 px-6 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                    >
                      Reject All
                    </button>
                  </div>
                </div>
                <button
                  onClick={rejectAll}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Preferences Modal */}
      {showPreferences && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-2xl flex items-center justify-center">
                    <Settings className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Cookie Preferences</h2>
                    <p className="text-gray-600 text-sm">Manage your cookie settings</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <X className="w-4 h-4 text-gray-600" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-4 mb-6">
                {cookieTypes.map((type) => (
                  <div key={type.id} className="bg-gray-50 rounded-2xl p-4 border border-gray-200">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className={`w-8 h-8 rounded-2xl flex items-center justify-center ${
                          type.id === 'necessary' ? 'bg-green-100' :
                          type.id === 'functional' ? 'bg-blue-100' :
                          type.id === 'analytics' ? 'bg-purple-100' : 'bg-amber-100'
                        }`}>
                          {type.id === 'necessary' && <Shield className="w-4 h-4 text-green-600" />}
                          {type.id === 'functional' && <Settings className="w-4 h-4 text-blue-600" />}
                          {type.id === 'analytics' && <Eye className="w-4 h-4 text-purple-600" />}
                          {type.id === 'marketing' && <Users className="w-4 h-4 text-amber-600" />}
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">{type.name}</h3>
                          <p className="text-sm text-gray-600">{type.description}</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={cookiePreferences[type.id]}
                          onChange={() => handlePreferenceChange(type.id)}
                          disabled={type.required}
                          className="sr-only peer"
                        />
                        <div className={`w-11 h-6 rounded-full peer ${
                          type.required ? 'bg-green-300' : 'bg-gray-200 peer-checked:bg-blue-600'
                        } peer-focus:ring-2 peer-focus:ring-blue-300 transition-colors`}>
                          <div className={`absolute top-0.5 left-0.5 bg-white border border-gray-300 rounded-full h-5 w-5 transition-transform ${
                            cookiePreferences[type.id] ? 'transform translate-x-5' : ''
                          } ${type.required ? 'bg-green-100' : ''}`} />
                        </div>
                      </label>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <div className="flex items-center space-x-4">
                        <span className="flex items-center space-x-1">
                          <Clock className="w-3 h-3" />
                          <span>Duration: {type.duration}</span>
                        </span>
                        {type.required && (
                          <span className="flex items-center space-x-1 text-green-600">
                            <Shield className="w-3 h-3" />
                            <span>Always active</span>
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="mt-3">
                      <p className="text-sm text-gray-600 mb-2">Examples:</p>
                      <div className="flex flex-wrap gap-2">
                        {type.examples.map((example, index) => (
                          <span key={index} className="px-2 py-1 bg-white rounded-lg text-xs text-gray-600 border">
                            {example}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 rounded-2xl p-4 border border-blue-200 mb-6">
                <div className="flex items-start space-x-3">
                  <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h4 className="font-semibold text-blue-900 mb-1">About Your Choices</h4>
                    <p className="text-blue-800 text-sm">
                      Necessary cookies are required for the website to function and cannot be switched off. 
                      You can update these preferences at any time by clicking the "Cookie Settings" link in the footer.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={savePreferences}
                  className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors flex items-center space-x-2"
                >
                  <Check className="w-4 h-4" />
                  <span>Save Preferences</span>
                </button>
                <button
                  onClick={acceptAll}
                  className="bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors"
                >
                  Accept All
                </button>
                <button
                  onClick={rejectAll}
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
                >
                  Reject All
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-indigo-700 rounded-2xl flex items-center justify-center">
                <Cookie className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Cookies Policy</h1>
                <p className="text-gray-600">Last updated: {lastUpdated}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button 
                onClick={() => setShowPreferences(true)}
                className="hidden md:flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
              >
                <Settings className="w-4 h-4" />
                <span>Cookie Settings</span>
              </button>
              <button className="hidden md:flex items-center space-x-2 bg-gray-100 hover:bg-gray-200 px-4 py-2 rounded-lg transition-colors">
                <Download className="w-4 h-4" />
                <span className="font-medium">Download PDF</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-8">
              <h3 className="font-bold text-gray-900 mb-4">Quick Navigation</h3>
              <nav className="space-y-2">
                {sections.map((section) => (
                  <button
                    key={section.id}
                    onClick={() => {
                      document.getElementById(section.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    {section.title}
                  </button>
                ))}
              </nav>

              {/* Quick Actions */}
              <div className="mt-8 space-y-3">
                <button 
                  onClick={() => setShowPreferences(true)}
                  className="w-full flex items-center justify-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                >
                  <Settings className="w-4 h-4" />
                  <span>Manage Cookies</span>
                </button>
                <button className="w-full flex items-center justify-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                  <Share2 className="w-4 h-4" />
                  <span>Share Policy</span>
                </button>
              </div>

              {/* Current Status */}
              <div className="mt-8 p-4 bg-green-50 rounded-lg border border-green-200">
                <div className="flex items-center space-x-3 mb-3">
                  <Shield className="w-5 h-5 text-green-600" />
                  <h4 className="font-semibold text-green-900">Your Privacy Choices</h4>
                </div>
                <p className="text-sm text-green-700 mb-3">
                  You can change your cookie preferences at any time using the settings panel.
                </p>
                <div className="flex items-center space-x-4 text-xs text-green-600">
                  <div className="flex items-center space-x-1">
                    <Lock className="w-3 h-3" />
                    <span>Encrypted</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Eye className="w-3 h-3" />
                    <span>Transparent</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
              {/* Summary Card */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white p-8">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <Cookie className="w-8 h-8" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Cookies & Tracking Technologies</h2>
                    <p className="text-blue-100">Understanding how we use cookies to enhance your experience</p>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-3 gap-6 mt-6">
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Shield className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Transparent</h3>
                    <p className="text-blue-100 text-sm">Clear cookie usage explanations</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Settings className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Controllable</h3>
                    <p className="text-blue-100 text-sm">Manage your preferences</p>
                  </div>
                  <div className="text-center">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3 backdrop-blur-sm">
                      <Lock className="w-6 h-6" />
                    </div>
                    <h3 className="font-bold text-lg">Secure</h3>
                    <p className="text-blue-100 text-sm">Encrypted data protection</p>
                  </div>
                </div>
              </div>

              {/* Policy Sections */}
              <div className="p-8">
                <div className="prose prose-lg max-w-none">
                  <div className="mb-8 p-6 bg-amber-50 border border-amber-200 rounded-2xl">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="text-amber-900 font-bold mb-2">Cookie Consent</h3>
                        <p className="text-amber-800 text-sm">
                          By using our website, you consent to the use of cookies as described in this policy. 
                          You can manage your cookie preferences at any time through the settings panel.
                        </p>
                      </div>
                    </div>
                  </div>

                  {sections.map((section) => (
                    <div key={section.id} id={section.id} className="mb-8 last:mb-0">
                      <button
                        onClick={() => toggleSection(section.id)}
                        className="flex items-center justify-between w-full text-left py-4 border-b border-gray-200 hover:bg-gray-50 -mx-4 px-4 rounded-lg transition-colors"
                      >
                        <h3 className="text-xl font-bold text-gray-900">{section.title}</h3>
                        {openSections[section.id] ? (
                          <ChevronUp className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        )}
                      </button>
                      
                      <div className={`mt-4 text-gray-700 leading-relaxed ${openSections[section.id] ? 'block' : 'hidden'}`}>
                        {section.content.split('\n\n').map((paragraph, index) => (
                          <p key={index} className="mb-4 last:mb-0">
                            {paragraph.split('**').map((text, i) => 
                              i % 2 === 1 ? (
                                <strong key={i} className="text-gray-900">{text}</strong>
                              ) : (
                                text
                              )
                            )}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Quick Actions */}
                  <div className="mt-12 flex flex-wrap gap-4">
                    <button 
                      onClick={() => setShowPreferences(true)}
                      className="flex items-center space-x-2 bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <Settings className="w-4 h-4" />
                      <span>Manage Cookie Settings</span>
                    </button>
                    <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-200 transition-colors">
                      <Download className="w-4 h-4" />
                      <span>Download Policy</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Resources */}
            <div className="grid md:grid-cols-3 gap-6 mt-8">
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-green-100 rounded-2xl flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Browser Settings</h4>
                <p className="text-gray-600 text-sm">
                  Learn how to manage cookies through your browser settings and privacy controls.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-purple-100 rounded-2xl flex items-center justify-center mb-4">
                  <Eye className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Third-Party Tools</h4>
                <p className="text-gray-600 text-sm">
                  Explore opt-out tools for analytics and advertising cookies from third parties.
                </p>
              </div>
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="w-12 h-12 bg-blue-100 rounded-2xl flex items-center justify-center mb-4">
                  <Info className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-gray-900 mb-2">Need Help?</h4>
                <p className="text-gray-600 text-sm">
                  Contact our privacy team for questions about cookies and your privacy choices.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiesPolicy;